import { component } from "picoapp";
import { type Context } from "../../state";
import { type FocusTrap, createFocusTrap } from "focus-trap";

const toggleModal = (
  node: HTMLElement,
  video: HTMLVideoElement,
  trap: FocusTrap
) => {
  node.classList.toggle("flex");
  node.classList.toggle("hidden");

  if (node.classList.contains("flex")) {
    if (!trap.active) {
      trap.activate();
    }

    if (video.paused) {
      video.play();
    }
  } else if (node.classList.contains("hidden")) {
    if (trap.active) {
      trap.deactivate();
    }

    if (!video.paused) {
      video.pause();
    }
  }
};

export const videoModal = component<Context>((node, context) => {
  const video = node.querySelector<HTMLVideoElement>("video");
  const closeButton = node.querySelector<HTMLButtonElement>(".js-close-button");

  if (!video) {
    return;
  }

  const trap = createFocusTrap(node, {
    initialFocus: video,
    fallbackFocus: video,
    setReturnFocus: video,
  });

  context.on("tbsp:button-clicked", ({ action, target }) => {
    if (action === "toggle-modal" && target === node.dataset.tbspTarget) {
      toggleModal(node, video, trap);
    }
  });

  const handleEscKey = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return;

    closeModal(e);
  };

  const handleToggleClick = (e: MouseEvent) => {
    if (e.target !== video) {
      toggleModal(node, video, trap);
    }
  };

  const closeModal = (e: Event) => {
    e.preventDefault();
    if (!node.classList.contains("hidden")) {
      e.stopPropagation();
      toggleModal(node, video, trap);
    }
  };

  node.addEventListener("click", handleToggleClick);
  window.addEventListener("keydown", handleEscKey);
  if (closeButton) {
    closeButton.addEventListener("click", closeModal);
  }

  return (node: HTMLElement) => {
    node.removeEventListener("click", handleToggleClick);
    window.removeEventListener("keydown", handleEscKey);
    if (closeButton) {
      closeButton.removeEventListener("click", closeModal);
    }
  };
});
